import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, EyeOff, AlertTriangle, CheckCircle, Shield, TrendingUp } from 'lucide-react';

export default function BrokerIntelligencePage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/broker-intelligence").then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div data-testid="broker-intel-loading"><Skeleton className="h-64 rounded-sm" /></div>;

  const summary = data?.summary || {};
  const brokers = data?.brokers || [];

  return (
    <div className="space-y-6" data-testid="broker-intelligence-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>Broker Intelligence</h1>
        <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>Dimension U — Non-Disclosure Probability Analysis</p>
        <p className="text-[10px] mt-0.5 italic" style={{ color: '#5C6B7A' }}>This data is invisible to brokers. MGA eyes only.</p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Brokers Tracked', value: summary.total_brokers ?? 0, icon: Shield, color: '#1A4C7C' },
          { label: 'Submissions', value: summary.total_submissions_tracked ?? 0, icon: TrendingUp, color: '#0D1B2A' },
          { label: 'Edits Detected', value: summary.total_edits_detected ?? 0, icon: Eye, color: '#E8A020' },
          { label: 'High Risk', value: summary.high_risk_submissions ?? 0, icon: AlertTriangle, color: '#E24B4A' },
          { label: 'Avg NDP Score', value: `${summary.avg_ndp_score ?? 0}/100`, icon: EyeOff, color: (summary.avg_ndp_score ?? 0) > 40 ? '#E8A020' : '#1D9E75' },
          { label: 'Avg Accuracy', value: `${((summary.avg_broker_accuracy ?? 0) * 100).toFixed(0)}%`, icon: CheckCircle, color: (summary.avg_broker_accuracy ?? 0) > 0.7 ? '#1D9E75' : '#E8A020' },
        ].map((kpi, i) => (
          <Card key={kpi.label} className="p-3 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{kpi.label}</span>
            </div>
            <p className="text-lg font-bold font-mono" style={{ color: '#0D1B2A' }}>{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Broker Cards */}
      <div className="space-y-3">
        {brokers.length === 0 ? (
          <Card className="p-8 rounded-sm bg-white text-center"><p style={{ color: '#5C6B7A' }}>No broker data yet. Submissions will populate this view.</p></Card>
        ) : brokers.map(broker => {
          const ndp = broker.avg_ndp_score || 0;
          const accuracy = broker.accuracy_score || 0;
          const risk = ndp >= 60 ? 'high' : ndp >= 30 ? 'medium' : 'low';
          const riskColors = { high: '#E24B4A', medium: '#E8A020', low: '#1D9E75' };
          return (
            <Card key={broker.broker_id} data-testid={`broker-${broker.broker_id}`}
              className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden">
              <div className="flex">
                {/* Risk indicator bar */}
                <div className="w-1.5" style={{ background: riskColors[risk] }} />
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>{broker.broker_name}</h3>
                      <p className="text-xs" style={{ color: '#5C6B7A' }}>{broker.broker_email}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase"
                      style={{ background: riskColors[risk] + '15', color: riskColors[risk] }}>
                      {risk} risk
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                    {/* NDP Score */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>NDP Score</p>
                      <div className="flex items-end gap-1 mt-1">
                        <span className="text-xl font-bold font-mono" style={{ color: riskColors[risk] }}>{ndp.toFixed(0)}</span>
                        <span className="text-[10px] mb-0.5" style={{ color: '#5C6B7A' }}>/100</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden mt-1" style={{ background: 'rgba(136,150,168,0.15)' }}>
                        <div className="h-full rounded-full" style={{ width: `${ndp}%`, background: riskColors[risk] }} />
                      </div>
                    </div>

                    {/* Accuracy */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Accuracy</p>
                      <p className="text-xl font-bold font-mono mt-1" style={{ color: accuracy > 0.7 ? '#1D9E75' : '#E8A020' }}>
                        {(accuracy * 100).toFixed(0)}%
                      </p>
                    </div>

                    {/* Submissions */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Submissions</p>
                      <p className="text-xl font-bold mt-1" style={{ color: '#0D1B2A' }}>{broker.total_submissions}</p>
                    </div>

                    {/* Edits */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Edits Detected</p>
                      <p className="text-xl font-bold mt-1" style={{ color: broker.total_edits > 0 ? '#E8A020' : '#0D1B2A' }}>{broker.total_edits}</p>
                    </div>

                    {/* Entities */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Entities</p>
                      <p className="text-xl font-bold mt-1" style={{ color: '#0D1B2A' }}>{broker.entities_submitted}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Legend */}
      <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <h4 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#0D1B2A' }}>About Dimension U</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs" style={{ color: '#5C6B7A' }}>
          <div><strong style={{ color: '#0D1B2A' }}>NDP Score (0-100):</strong> Non-Disclosure Probability. Higher = more likely the broker is under-declaring. Computed from edit patterns, declaration consistency, and entity database comparison.</div>
          <div><strong style={{ color: '#0D1B2A' }}>Accuracy Score:</strong> Historical consistency between declared values and entity known values. Lower scores indicate systematic under-reporting patterns.</div>
          <div><strong style={{ color: '#0D1B2A' }}>Version 1 Preservation:</strong> The first values entered for each field are permanently stored. Every subsequent edit is tracked and compared.</div>
          <div><strong style={{ color: '#0D1B2A' }}>Invisible to Brokers:</strong> Brokers never see their NDP score, accuracy rating, or edit tracking. This data is MGA intelligence only.</div>
        </div>
      </Card>
    </div>
  );
}
